export const OTP_RESEND_COOLDOWN_MS = 60_000;
export const OTP_RESEND_MAX_BEFORE_LOCKOUT = 3;
export const OTP_LOCKOUT_MS = 10 * 60_000;

export type OtpResendPurpose = 'email_verification' | 'password_reset';

export type OtpResendState = {
  resendCount: number;
  lastSentAt: Date | string | null;
  lockedUntil: Date | string | null;
};

export type OtpResendEvaluation = {
  allowed: boolean;
  nextResendAt: Date | null;
  resendCount: number;
  maxResends: number;
  cooldownSeconds: number;
  lockedUntil: Date | null;
  message?: string;
};

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
}

/** Evaluate whether another OTP email may be sent (resend only). */
export function evaluateOtpResendAllowance(
  state: OtpResendState,
  options: { isResend: boolean; now?: Date } = { isResend: true },
): OtpResendEvaluation {
  const now = options.now ?? new Date();
  const lastSentAt = toDate(state.lastSentAt);
  let lockedUntil = toDate(state.lockedUntil);
  let resendCount = state.resendCount;

  if (lockedUntil && lockedUntil.getTime() <= now.getTime()) {
    lockedUntil = null;
    resendCount = 0;
  }

  const base: OtpResendEvaluation = {
    allowed: true,
    nextResendAt: null,
    resendCount,
    maxResends: OTP_RESEND_MAX_BEFORE_LOCKOUT,
    cooldownSeconds: OTP_RESEND_COOLDOWN_MS / 1000,
    lockedUntil,
  };

  if (!options.isResend) {
    return base;
  }

  if (lockedUntil && lockedUntil.getTime() > now.getTime()) {
    return {
      ...base,
      allowed: false,
      nextResendAt: lockedUntil,
      lockedUntil,
      resendCount,
      message: 'Too many verification emails. Try again later.',
    };
  }

  if (resendCount >= OTP_RESEND_MAX_BEFORE_LOCKOUT) {
    const lock = new Date(now.getTime() + OTP_LOCKOUT_MS);
    return {
      ...base,
      allowed: false,
      nextResendAt: lock,
      lockedUntil: lock,
      resendCount,
      message: 'Too many verification emails. Try again in 10 minutes.',
    };
  }

  if (lastSentAt) {
    const nextAllowed = new Date(lastSentAt.getTime() + OTP_RESEND_COOLDOWN_MS);
    if (nextAllowed.getTime() > now.getTime()) {
      return {
        ...base,
        allowed: false,
        nextResendAt: nextAllowed,
        resendCount,
        message: 'Please wait before requesting another code.',
      };
    }
  }

  return base;
}

/** State after a successful OTP send. */
export function nextOtpResendStateAfterSend(
  state: OtpResendState,
  options: { isResend: boolean; now?: Date },
): OtpResendState {
  const now = options.now ?? new Date();
  const evaluation = evaluateOtpResendAllowance(state, options);
  let resendCount = evaluation.resendCount;
  let lockedUntil = evaluation.lockedUntil;

  if (options.isResend) {
    resendCount += 1;
    if (resendCount >= OTP_RESEND_MAX_BEFORE_LOCKOUT) {
      lockedUntil = new Date(now.getTime() + OTP_LOCKOUT_MS);
    }
  }

  return {
    resendCount,
    lastSentAt: now,
    lockedUntil,
  };
}

export function serializeOtpResendStatus(
  evaluation: OtpResendEvaluation,
): {
  nextResendAt: string | null;
  resendCount: number;
  maxResends: number;
  cooldownSeconds: number;
  lockedUntil: string | null;
  canResend: boolean;
} {
  return {
    nextResendAt: evaluation.nextResendAt?.toISOString() ?? null,
    resendCount: evaluation.resendCount,
    maxResends: evaluation.maxResends,
    cooldownSeconds: evaluation.cooldownSeconds,
    lockedUntil: evaluation.lockedUntil?.toISOString() ?? null,
    canResend: evaluation.allowed,
  };
}
